import type { CSSProperties } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { absCenter } from '@sorare/core/src/lib/style';

import locked from '#src/assets/locked.webp';

import { Tile } from '../../HotStreakTile/Tile';
import { DiamondBaseColor } from '../../HotStreakTile/Tile/utils';
import { DIAMOND_HEIGHT, DIAMOND_WIDTH } from '../../utils';
import nbaLocked from '../assets/nbaLocked.webp';
import nbaStadiumFire from '../assets/nbaStadiumFire.webp';
import stadiumFire from '../assets/stadiumFire.webp';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: -1;
  width: ${DIAMOND_WIDTH}px;
  height: ${DIAMOND_HEIGHT}px;
`;

const LockedTile = styled.div`
  ${absCenter}
  width: 260%;
  height: 260%;
  background: var(--background-image) no-repeat center / cover;
`;

const fireIconAnimation = keyframes`
  0% {
    transform: translateY(0) translateX(-50%);
  }
  50% {
    transform: translateY(4px) translateX(-50%);
  }
  100% {
    transform: translateY(0) translateX(-50%);
  }
`;

const FireIcon = styled.div<{ $animated: boolean }>`
  width: 260%;
  height: 260%;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 12px;
  background: var(--background-image) no-repeat center / contain;
  ${({ $animated }) =>
    $animated &&
    css`
      animation: ${fireIconAnimation} 3s ease-in-out infinite;
    `}
`;

type Props = {
  isLocked: boolean;
  onClick?: () => void;
  animated?: boolean;
  style?: CSSProperties;
  viewTransitionName?: string;
};

export const FireTile = ({
  isLocked,
  onClick,
  animated = true,
  style,
  viewTransitionName,
}: Props) => {
  const { sport } = useSportContext();
  const isNba = sport === Sport.NBA;

  if (isLocked) {
    return (
      <Wrapper style={style}>
        <LockedTile
          style={{
            backgroundImage: `url(${isNba ? nbaLocked : locked})`,
          }}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper style={style}>
      <ButtonBase onClick={onClick}>
        {viewTransitionName ? (
          <div style={{ viewTransitionName }}>
            <Tile color={DiamondBaseColor.orange} size={2} borderAnimated />
            <FireIcon
              $animated={animated}
              style={{
                backgroundImage: `url(${isNba ? nbaStadiumFire : stadiumFire})`,
              }}
            />
          </div>
        ) : (
          <>
            <Tile color={DiamondBaseColor.orange} size={2} borderAnimated />
            <FireIcon
              $animated={animated}
              style={{
                backgroundImage: `url(${isNba ? nbaStadiumFire : stadiumFire})`,
              }}
            />
          </>
        )}
      </ButtonBase>
    </Wrapper>
  );
};
